import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { adminApi, type RequestDirection, type RequestLogRow } from '../api/admin';

// M4-E3-S1-T2: RequestLog ビューア (シンプル版)。検索フィルタは direction のみ。
// 大量データ対応 (M4-E3-S1-T2 の正規版) は M4 着手時にページング / 日付フィルタを追加する。

const directions: RequestDirection[] = [
  'slack_in',
  'device_out',
  'device_in',
  'slack_out',
  'peer_message',
  'vscode_origin',
];

export function RequestLogsPage() {
  const [direction, setDirection] = useState<RequestDirection | ''>('');
  const query = useQuery({
    queryKey: ['request-logs', direction],
    queryFn: () =>
      adminApi.listRequestLogs({
        direction: direction || undefined,
        limit: 100,
      }),
    refetchInterval: 5_000,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <section className="k-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>Request Logs</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label htmlFor="direction" className="k-muted" style={{ fontSize: 13 }}>
              direction:
            </label>
            <select
              id="direction"
              value={direction}
              onChange={(e) => setDirection((e.target.value || '') as RequestDirection | '')}
            >
              <option value="">all</option>
              {directions.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="k-muted" style={{ marginTop: 4 }}>
          最新 100 件。5 秒毎に自動更新。Phase 2 で日付フィルタ / ページングを追加予定 (M4-E3-S1-T2)。
        </p>
        {query.isLoading && <p>Loading…</p>}
        {query.error && (
          <p className="k-error">{(query.error as Error).message}</p>
        )}
        {query.data && <LogTable rows={query.data} />}
      </section>
    </div>
  );
}

function LogTable({ rows }: { rows: RequestLogRow[] }) {
  if (rows.length === 0) {
    return <p className="k-muted">該当ログなし。</p>;
  }
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
          <th style={th}>id</th>
          <th style={th}>created</th>
          <th style={th}>direction</th>
          <th style={th}>device</th>
          <th style={th}>request_id</th>
          <th style={th}>payload (head)</th>
          <th style={th}>meta</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
            <td style={td}>{row.id}</td>
            <td style={td}>{new Date(row.createdAt).toLocaleString()}</td>
            <td style={td}>
              <code
                style={{
                  fontSize: 12,
                  background: directionBg(row.direction),
                  padding: '2px 6px',
                  borderRadius: 4,
                }}
              >
                {row.direction}
              </code>
            </td>
            <td style={td}>{row.deviceId ?? '—'}</td>
            <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>
              {row.requestId.slice(0, 12)}…
            </td>
            <td style={{ ...td, maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.payload.slice(0, 120)}
            </td>
            <td style={{ ...td, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, color: '#475569' }}>
              {JSON.stringify(row.meta)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function directionBg(d: RequestDirection): string {
  switch (d) {
    case 'device_out':
      return '#dbeafe';
    case 'device_in':
      return '#dcfce7';
    case 'slack_in':
    case 'slack_out':
      return '#fef9c3';
    default:
      return '#f1f5f9';
  }
}

const th = { padding: '8px 12px', fontWeight: 600, fontSize: 13 } as const;
const td = { padding: '8px 12px', fontSize: 14, verticalAlign: 'top' } as const;
